// Type-safe translation helpers. Prefer these in new code: a typo in the key
// becomes a compile error instead of a raw key ID rendered in the UI.
//
//   const t = useT()                      // inside components
//   t('GENERAL.SOMETHING_WENT_WRONG')
//
//   t('GENERAL.CANCEL')                   // outside components (helpers etc.)
//
// For IDs built at runtime (`SCHEDULE.${day}`), type the lookup table instead:
//   const labels = {mon: 'SCHEDULE.MONDAY'} satisfies Record<Day, TranslationKey>
import {useCallback} from 'react'
import {useIntl} from 'react-intl'

import {getActiveMessages} from './messageRegistry'
import type {TranslationKey} from './translationKeys'

// Primitive-only values keep formatMessage's return type a plain string.
type Values = Record<string, string | number | boolean | Date | null | undefined>

export type {TranslationKey}

// Module-level variant for non-React code. Falls back to the raw message (or
// key) if called before the intl context is ready.
export const t = (id: TranslationKey, values?: Values): string => {
  if (typeof window !== 'undefined' && window.intl?.formatMessage) {
    return window.intl.formatMessage({id}, values)
  }
  return getActiveMessages()[id] ?? id
}

// Hook variant — reactive to the intl context, use inside components.
export const useT = () => {
  const intl = useIntl()
  return useCallback(
    (id: TranslationKey, values?: Values): string => intl.formatMessage({id}, values),
    [intl]
  )
}
